import { cn } from '@heroui/react'
import { Button } from '@radix-ui/themes'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { useAuth } from 'hooks/useAuth'
import { Trash2 } from 'lucide-react'
import { toast } from 'react-toastify'
import { $fetch, useFetch } from 'utils/fetch'
import { toDtmy } from 'utils/formatting/toDtmy'
import { TipTapNoteEditor } from './TipTapNoteEditor'

export function NotesThread({ callId }) {
  const queryClient = useQueryClient()
  const { user, org } = useAuth()

  const { data: notes = [], isLoading } = useFetch(`/v1/notes/call/${callId}`, {
    queryKey: ['notes', callId],
    enabled: !!callId,
    dataKey: ['data', 'notes'],
  })

  const { data: orgMembers = [], isLoading: isLoadingOrgMembers } = useFetch(`/orgs/${org?.id}/members`, {
    queryKey: ['orgMembers', org?.id],
    enabled: !!org?.id,
  })

  // Create note mutation
  const createNote = useMutation({
    mutationFn: async (content) => {
      return await $fetch('/v1/notes', {
        method: 'POST',
        body: {
          c_id: callId,
          comment: content,
        },
      })
    },
    onSuccess: () => {
      queryClient.invalidateQueries(['notes', callId])
      toast.success('Note added')
    },
    onError: () => {
      toast.error('Failed to add note')
    },
  })

  // Delete note mutation
  const deleteNote = useMutation({
    mutationFn: async (noteId) => {
      return await $fetch(`/v1/notes/${noteId}`, {
        method: 'DELETE',
      })
    },
    onSuccess: () => {
      queryClient.invalidateQueries(['notes', callId])
      toast.success('Note deleted')
    },
    onError: () => {
      toast.error('Failed to delete note')
    },
  })

  const handleDelete = (noteId) => {
    if (window.confirm('Are you sure you want to delete this note?')) {
      deleteNote.mutate(noteId)
    }
  }

  // Legacy note compatibility - if there's a single note string
  const legacyNote = typeof notes === 'string' ? notes : null

  if (isLoading) {
    return <div className="p-4 text-center text-gray-500">Loading notes...</div>
  }

  return (
    <div className="flex flex-col gap-4">
      {/* Notes list */}
      <div className="flex flex-col gap-3">
        {legacyNote
          ? (
        // Display legacy note
              <div className="rounded-lg border border-gray-200 bg-gray-50 p-4">
                <div className="prose prose-sm max-w-none" dangerouslySetInnerHTML={{ __html: legacyNote }} />
              </div>
            )
          : notes?.length > 0
            ? (
          // Display thread of notes
                notes.map((note) => {
                  const isOwner = note.user_id === user?.user?.id

                  return (
                    <div
                      key={note.id}
                      className={cn(
                        'rounded-lg border p-4',
                        isOwner
                          ? 'border-blue-200 bg-blue-50'
                          : `border-gray-200 bg-gray-50`,
                      )}
                    >
                      <div className="mb-2 flex items-start justify-between">
                        <div className="flex items-center gap-2">
                          <div className={`
                            flex size-8 items-center justify-center rounded-full
                            bg-gradient-to-br from-blue-400 to-purple-600
                            text-sm font-medium text-white
                          `}
                          >
                            {(note.author || '?').charAt(0).toUpperCase()}
                          </div>
                          <div>
                            <div className="text-sm font-medium">{note.author || 'Unknown'}</div>
                            <div className="text-xs text-gray-500">
                              {toDtmy(new Date(note.created_at))}
                            </div>
                          </div>
                        </div>
                        {isOwner && (
                          <div className="flex gap-1">
                            <Button
                              size="1"
                              variant="ghost"
                              color="red"
                              onClick={() => handleDelete(note.id)}
                              style={{ cursor: 'pointer' }}
                            >
                              <Trash2 size={14} />
                            </Button>
                          </div>
                        )}
                      </div>
                      <div
                        className="prose prose-sm max-w-none"
                        dangerouslySetInnerHTML={{ __html: note.comment }}
                      />
                    </div>
                  )
                })
              ) : (
          // Empty state
                <div className={`
                  rounded-lg border border-dashed border-gray-300 bg-gray-50 p-8
                  text-center
                `}
                >
                  <p className="text-gray-500">No notes yet. Be the first to add one!</p>
                </div>
              )}
      </div>

      {/* Add new note editor */}
      {!legacyNote && !isLoadingOrgMembers && (
        <div className="mt-4">
          <TipTapNoteEditor
            onSubmit={createNote.mutate}
            placeholder="Add a note... Use @ to mention someone"
            orgMembers={orgMembers}
          />
        </div>
      )}

      <style jsx global>
        {`
        .mention {
          background-color: rgba(59, 130, 246, 0.1);
          color: #3b82f6;
          border-radius: 0.25rem;
          padding: 0.125rem 0.25rem;
          font-weight: 500;
        }
      `}
      </style>
    </div>
  )
}
